/**
 * Finding a door, like X's people search (rulings R-0904-3, R-0905-9). Split
 * module, the barrel re-exports.
 *
 * One read. `typeahead` is Nob's own index — everybody it already knows, free,
 * asked on every keystroke past the debounce. There is no call for X's own
 * search: the dialog's "Search X" row is a link that opens X's people search
 * in a new tab, and nothing of ours is asked on that road.
 */
import { bareHandle, call, get, textOf } from "./core";
import { ownOriginAvatar } from "./knock";

/** "door open" / "waiting room" / nothing — the third is the honest state for most of X. */
export type PersonBadge = "door" | "waiting" | null;

/**
 * One person, exactly as either read describes them. A strict allowlist on the
 * wire, and a strict one here: no id, no follower count, no rank ever reaches
 * a component from this parser, whatever a payload grows.
 */
export type PersonRow = {
  handle: string;
  display_name: string;
  /** A path on our own origin, or null — `ownOriginAvatar` is the second gate (FR-3.8.2). */
  avatar: string | null;
  badge: PersonBadge;
};

export type TypeaheadResult =
  | { ok: true; rows: PersonRow[] }
  | { ok: false; code: "UNAUTHENTICATED" | "RATE_LIMITED" | "UNKNOWN" };

function personRows(raw: unknown): PersonRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const row = entry as Record<string, unknown>;
    const handle = bareHandle(textOf(row["handle"]));
    if (!handle) return [];
    const name = textOf(row["display_name"]);
    const badge = row["badge"];
    return [
      {
        handle,
        display_name: name || `@${handle}`,
        avatar: ownOriginAvatar(row["avatar"]),
        badge: badge === "door" || badge === "waiting" ? badge : null,
      },
    ];
  });
}

function refused(status: number): "UNAUTHENTICATED" | "RATE_LIMITED" | "UNKNOWN" {
  if (status === 401) return "UNAUTHENTICATED";
  if (status === 429) return "RATE_LIMITED";
  return "UNKNOWN";
}

/**
 * Everybody Nob knows, matched on a name or a handle. Needs a session; a query
 * under two characters is answered empty by the server rather than refused, so
 * the caller may fire this on every keystroke past the debounce.
 */
export async function typeahead(query: string): Promise<TypeaheadResult> {
  const r = await call(() => get(`/api/v1/directory/typeahead?q=${encodeURIComponent(query)}`));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (!r.res.ok) return { ok: false, code: refused(r.res.status) };
  return { ok: true, rows: personRows((r.body as Record<string, unknown>)["rows"]) };
}
